import type { PaymentTerm, PenciledInputOfferType } from '@makemydeal/dr-platform-types';
import { OFFER_ENCRYPTION_FIELDS, SpecialPaymentFormula } from '@makemydeal/dr-platform-types';
import type { AnyAction } from 'redux';
import * as types from '../types/actionTypes';
import * as selectors from '../selectors';
import { convertRatesFromSourceType } from '../utils';
import { applyManualTaxes } from '../utils/taxUtils';
import { isSkipNullCashIncentiveIdsEnabled } from '../utils/incentives';
import { initialState } from './initialState';
import { normalizeLoadedPaymentIncentives } from './incentivesStateUtils';
import { getLoadPayloadFeatureToggles } from '../utils/loadPayloadFeatureToggles';
import type { PenciledInputsState } from '../types/commonTypes';

export const totalFinanced = (state: number = initialState.totalFinanced, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return action.payload.totalFinanced || 0;
        case types.SYNC_MESSAGE:
            return action.message.offer.totalFinanced || state;
        default:
            return state;
    }
};

export const monthlyPayment = (state: number | undefined = initialState.monthlyPayment, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return action.payload.monthlyPayment || 0;
        case types.SYNC_MESSAGE:
            return action.message.offer.monthlyPayment || state;
        default:
            return state;
    }
};

export const monthlyPaymentWithoutProducts = (
    state: number | undefined = initialState.monthlyPaymentWithoutProducts,
    action: AnyAction
): number | undefined => {
    switch (action.type) {
        case types.INIT_OFFER:
            return action.payload.monthlyPaymentWithoutProducts || 0;
        case types.SYNC_MESSAGE:
            return action.message.offer.monthlyPaymentWithoutProducts || state;
        default:
            return state;
    }
};

export const payment = (state: PaymentTerm = initialState.payment, action: AnyAction) => {
    switch (action.type) {
        case types.INIT_OFFER:
            return (
                normalizeLoadedPaymentIncentives(action.payload.payment, isSkipNullCashIncentiveIdsEnabled(action.payload)) || {}
            );
        case types.TOGGLE_RATE_TYPE: {
            const { rateType } = action.payload;
            const { transformedBuyRate, transformedSellRate } = convertRatesFromSourceType(
                rateType,
                state.rateType,
                state.buyRate,
                state.sellRate
            );
            return {
                ...state,
                rateType: rateType,
                sellRate: transformedSellRate,
                buyRate: transformedBuyRate
            };
        }
        default:
            return state;
    }
};

export const buildEncryptionPaymentState = (
    state: string | undefined = initialState[OFFER_ENCRYPTION_FIELDS.PAYMENT],
    action: AnyAction
) => {
    switch (action.type) {
        case types.INIT_OFFER:
        case types.PAYMENT_RECEIVED:
            if (action.meta?.isStalePaymentResponse) {
                return state;
            }
            if (
                action.meta?.originalAction?.meta?.middleware?.nonSelectedTermHonoringSV ||
                action.meta?.originalAction?.meta?.paymentGrid?.isMainPayment === false
            ) {
                return state;
            }
            return action.payload?.[OFFER_ENCRYPTION_FIELDS.PAYMENT] || '';
        case types.OFFER_SUBMISSION_SUCCESS:
            return state || action.payload?.offer?.[OFFER_ENCRYPTION_FIELDS.PAYMENT] || '';
        case types.SYNC_MESSAGE:
            return action.message?.offer?.[OFFER_ENCRYPTION_FIELDS.PAYMENT] || state || '';
        default:
            return state;
    }
};

export const penciledInputsPassthroughReducer = (
    state: PenciledInputsState | undefined = initialState.penciledInputs,
    action: AnyAction
) => {
    switch (action.type) {
        case types.INIT_SUCCESS: {
            const offer = action.payload.offer;
            const offerType = offer.type as PenciledInputOfferType;
            const creditScore = selectors.getCreditScore(offer);
            const taxItems = selectors.getPaymentTaxItems(offer);
            const flatTaxItem = selectors.getFlatTaxItem(taxItems);
            // When the override tax fix is enabled and the reloaded offer has no FLAT tax item
            // (e.g. a Sales View deal where the manual total tax was persisted but not re-emitted
            // as a FLAT-named tax item), preserve the manualTotalTax already recovered during the
            // INIT_PENCIL_SUCCESS hydration pass instead of overwriting it back to undefined.
            const enableOverrideTaxFixes = getLoadPayloadFeatureToggles(action.payload)?.overrideTaxFixes_US1984109;
            const resolvedManualTotalTax =
                flatTaxItem?.taxAmount != null
                    ? flatTaxItem.taxAmount
                    : enableOverrideTaxFixes
                    ? state?.byOfferType?.[offerType]?.manualTotalTax
                    : flatTaxItem?.taxAmount;
            const isTaxUpfront = !selectors.getPaymentTaxCapped(offer);
            const feesOverride = selectors.getInitialFeesOverride(offer);
            const isFordFlexBuyPayment = selectors.isFordFlexBuyPayment(offer);
            const offerPayment = offer?.payment;

            return {
                ...state,
                ...{
                    manualCreditScore: creditScore?.isManualCreditScore ? creditScore.creditTierTargetScore : undefined,
                    byOfferType: {
                        ...state?.byOfferType,
                        [offerType]: {
                            ...state?.byOfferType![offerType],
                            manualTotalTax: resolvedManualTotalTax,
                            manualTotalTaxEdited: resolvedManualTotalTax !== undefined,
                            taxOverrideUpfront: isTaxUpfront,
                            feesOverride,
                            feesDropped: offer?.cash?.feesDropped || [],
                            ...(isFordFlexBuyPayment && {
                                specialPaymentFormula: SpecialPaymentFormula.FordFlexBuy,
                                initialPaymentDiscount: offerPayment.initialPaymentDiscount
                                    ? [offerPayment.initialPaymentDiscount]
                                    : undefined,
                                comparableTermPaymentDetail: offerPayment?.comparableTermPaymentDetail,
                                firstTermPaymentDetail: offerPayment?.firstTermPaymentDetail,
                                secondTermPaymentDetail: offerPayment?.secondTermPaymentDetail
                            })
                        }
                    }
                }
            };
        }

        case types.UPDATE_TAX_ITEMS: {
            const { offerType, manualTaxItems } = action.payload as { offerType: PenciledInputOfferType; manualTaxItems: any };
            const taxItems = state?.byOfferType![offerType].taxItems;

            return {
                ...state,
                ...{
                    byOfferType: {
                        ...state?.byOfferType,
                        [offerType]: {
                            ...state?.byOfferType![offerType],
                            taxItems: applyManualTaxes(manualTaxItems, taxItems!)
                        }
                    }
                }
            };
        }

        case types.RESET_TAX_OVERRIDES: {
            const { offerType } = action.payload as { offerType: PenciledInputOfferType };

            return {
                ...state,
                ...{
                    byOfferType: {
                        ...state?.byOfferType,
                        [offerType]: {
                            ...state?.byOfferType![offerType],
                            taxItems: [],
                            taxOverrideUpfront: undefined
                        }
                    }
                }
            };
        }

        case types.CLEAR_TAX_AMOUNTS: {
            const { offerType } = action.payload as { offerType: PenciledInputOfferType };

            return {
                ...state,
                ...{
                    byOfferType: {
                        ...state?.byOfferType,
                        [offerType]: {
                            ...state?.byOfferType![offerType],
                            taxItems: [],
                            taxOverrideUpfront: undefined,
                            manualTotalTax: undefined
                        }
                    }
                }
            };
        }

        default: {
            return state;
        }
    }
};
